import { action, computed, observable } from 'mobx'
import analytics from '../../src/stores/analytics'
import RootStore from '../../src/stores/RootStore'
import { filterTreatmentIdsByPro } from '../helpers/professionalBookingHelper'
import { abbreviatedUserName } from '../lib/utils'
import { $CategoryModel } from '../stores/CategoryStore/CategoryModel'
import { $TreatmentModel } from '../stores/TreatmentStore/TreatmentModel'

export class ViewCategoryViewModel {
  store: RootStore
  @observable category: $CategoryModel

  constructor(store: RootStore) {
    this.store = store
  }

  getTreatmentFromId = (treatmentId: string) => {
    return this.treatmentsWithLiquidityData.find(t => t._id === treatmentId)
  }

  // Return list of treatments from current category
  @computed get treatments(): Array<$TreatmentModel> {
    const {
      treatmentStore,
      checkoutStore,
    } = this.store

    if (!this.category) return []

    const ids = filterTreatmentIdsByPro(this.category?.data.treatmentIds, checkoutStore.provisionalProfessional)
    return treatmentStore.findItemByIds(ids)
  }

  get categoryText() {
    const { checkoutStore: { provisionalProfessional } } = this.store
    if (provisionalProfessional) return `${provisionalProfessional.firstName}'s categories`
    return 'All categories'
  }

  get professionalTreatmentText() {
    const { checkoutStore: { provisionalProfessional } } = this.store

    return `${abbreviatedUserName(provisionalProfessional)}'s ${this.category?.data.name.toLocaleLowerCase()} treatments`
  }

  get headerText() {
    const { checkoutStore: { provisionalProfessional } } = this.store

    if (provisionalProfessional) return this.professionalTreatmentText

    return this.category?.data.description
  }

  @action.bound async fetchTreatments() {
    const { treatmentStore, checkoutStore, remoteConfig } = this.store
    const treatmentIds = [...this.category.data.treatmentIds]

    const useTestPrices = !!analytics.ABTest('testPrices')

    const variables: Record<string, any> = {
      filter: {
        _id: { $in: treatmentIds },
        regions: { $in: checkoutStore.bookingRegionOrDefault },
      },
      extra: { useTestPrices },
    }

    const fetchProAvailabilityConfig = (remoteConfig.availabilityLabelConfig || {})[this.category.data.name]
    const bookingWithPro = !!checkoutStore.provisionalProfessional

    if (fetchProAvailabilityConfig && !bookingWithPro) {
      variables.postcodeId = checkoutStore.booking.data.postcodeId
    }

    await treatmentStore.fetchItems_api({
      variables,
    })
  }

  @computed get treatmentsWithLiquidityData() {
    const { remoteConfig } = this.store

    const categoryName = this.category?.data.name
    const treatmentAvailabilityConfig = (remoteConfig.availabilityLabelConfig || {})[categoryName]

    const fetchProAvailabilityConfig = {
      lowAvailabilityMin: 14,
      lowAvailabilityThreshold: 0.9,
      comingSoonThreshold: 0,
      ...treatmentAvailabilityConfig,
    }

    const totalNumPros = this.treatments.reduce((a, b) => a + (b.data.numberOfPros || 0), 0)
    const averageNumPros = totalNumPros / this.treatments.length

    // Work out thresholds for "low availability" tag
    const lowAvailabilityThreshold = (averageNumPros || 1) * fetchProAvailabilityConfig.lowAvailabilityThreshold
    const lowAvailabilityMin = fetchProAvailabilityConfig.lowAvailabilityMin || 14

    // If the total number of pros availabile is 0 then set to -1, which will cause all tags to be "low availability"
    const comingSoonThreshold = totalNumPros === 0 ? -1 : fetchProAvailabilityConfig.comingSoonThreshold

    const treatmentsWithLiquidityData = this.treatments.map(t => {
      const { numberOfPros } = t.data

      const hasNumberOfPros = typeof numberOfPros === 'number'

      return {
        ...t.data,
        comingSoon: hasNumberOfPros && numberOfPros <= comingSoonThreshold,
        lowAvailability: hasNumberOfPros
          && numberOfPros > comingSoonThreshold
          && numberOfPros < lowAvailabilityMin
          && numberOfPros <= lowAvailabilityThreshold,
      }
    }).sort((a, b) => {
      const aScore = (a.comingSoon ? -10 : 0) + (a.lowAvailability ? -5 : 0)
      const bScore = (b.comingSoon ? -10 : 0) + (b.lowAvailability ? -5 : 0)
      return bScore - aScore
    })

    return treatmentsWithLiquidityData
  }
}